import { Injectable } from '@nestjs/common';
import { Repository } from 'typeorm';
import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { InjectRepository } from '@nestjs/typeorm';
import { EmployeeTypes } from './employee-type.entity';
import { EmployeeTypesDTO } from './dto/employee-type.dto';
import { EmployeeTypesAdapter } from './dto/employee-type.adapter';
import { EmployeeTypesResponseModel, AllEmployeeTypesResponse } from '@gtpl/shared-models/hrms';
import { EmployeeTypesIdRequest } from './dto/employee-type.request';


@Injectable()
export class EmployeeTypesService {

    constructor(
        @InjectRepository(EmployeeTypes)
        private repository: Repository<EmployeeTypes>,
        private adapter: EmployeeTypesAdapter,
    ) { }

    async getEmployeeTypesWithoutRelations(employeeTypes: string): Promise<EmployeeTypes> {
        const response = await this.repository.findOne({
            where: { employeeTypes: employeeTypes },
        });
        if (response) {
            return response;
        } else {
            return null;
        }
    }

    async createEmployeeTypes(dto: EmployeeTypesDTO, isUpdate: boolean): Promise<EmployeeTypesResponseModel> {
        try {
            if (!isUpdate) {
                const entity = await this.getEmployeeTypesWithoutRelations(dto.employeeTypes);
                if (entity) {
                    throw new ErrorResponse(11104, 'EmployeeTypes already exists');
                }
            } else {
                const entity = await this.getEmployeeTypesWithoutRelations(dto.employeeTypes);
                if (entity) {
                    if (entity.employeeTypesId != dto.employeeTypesId) {
                        throw new ErrorResponse(11104, 'EmployeeTypes already exists');
                    }
                }
            }
            const convertedDto: EmployeeTypes = this.adapter.convertDtoToEntity(dto);
            const savedEntity: EmployeeTypes = await this.repository.save(convertedDto);
            const savedDto: EmployeeTypesDTO = this.adapter.convertEntityToDto(savedEntity);
            if (savedDto) {
                const response = new EmployeeTypesResponseModel(true, 1, isUpdate ? 'EmployeeTypes updated successfully' : 'EmployeeTypes created successfully', savedDto);
                return response;
            } else {
                throw new ErrorResponse(11106, 'EmployeeTypes saved but there is an issue while converting it into DTO');
            }
        } catch (error) {
            throw error;
        }
    }

    async getAllEmployeeTypes(): Promise<AllEmployeeTypesResponse> {
        try {
            const dtos: EmployeeTypesDTO[] = [];
            const entities: EmployeeTypes[] = await this.repository.find({ order: { createdAt: 'DESC' } });
            if (entities) {
                entities.forEach(entity => {
                    const Dto: EmployeeTypes = this.adapter.convertEntityToDto(entity);
                    dtos.push(Dto);
                });
                const response = new AllEmployeeTypesResponse(true, 1, 'EmployeeTypess Retrieved Successfully', dtos);
                return response;
            } else {
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        } catch (error) {
            throw error;
        }
    }

    async getAllActiveEmployeeTypes(): Promise<AllEmployeeTypesResponse> {
        try {
            const dtos: EmployeeTypesDTO[] = [];
            const entities: EmployeeTypes[] = await this.repository.find({ order: { employeeTypes: 'ASC' }, where: { isActive: true } });
            if (entities) {
                entities.forEach(entity => {
                    const Dto: EmployeeTypes = this.adapter.convertEntityToDto(entity);
                    dtos.push(Dto);
                });
                const response = new AllEmployeeTypesResponse(true, 1, 'EmployeeTypes Retrieved Successfully', dtos);
                return response;
            } else {
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        } catch (error) {
            throw error;
        }
    }

    async getEmployeeTypesByEmpTypId(id: number): Promise<EmployeeTypes> {
        const response = await this.repository.findOne({ where: { employeeTypesId: id } });
        if (response) {
            return response;
        } else {
            return null;
        }
    }

    async activateOrDeactivateEmployeeTypes(req: EmployeeTypesIdRequest): Promise<EmployeeTypesResponseModel> {
        try {
            const empTypEsxists = await this.getEmployeeTypesByEmpTypId(req.employeeTypesId);
            if (empTypEsxists) {
                if (req.versionFlag != empTypEsxists.versionFlag) {
                    throw new ErrorResponse(10113, 'Someone updated the current employeeTypes informations. Refresh and try again');
                } else {
                    const empTypStatus = await this.repository.update(
                        { employeeTypesId: req.employeeTypesId },
                        { isActive: req.isActive, updatedUser: req.updatedUser }
                    );
                    if (empTypEsxists.isActive) {
                        if (empTypStatus.affected) {
                            const response: EmployeeTypesResponseModel = new EmployeeTypesResponseModel(true, 10115, 'EmployeeTypes de-activated successfully');
                            return response;
                        } else {
                            throw new ErrorResponse(10111, 'EmployeeTypes is already de-activated');
                        }
                    } else {
                        if (empTypStatus.affected) {
                            const response: EmployeeTypesResponseModel = new EmployeeTypesResponseModel(true, 10114, 'EmployeeTypes activated successfully');
                            return response;
                        } else {
                            throw new ErrorResponse(10112, 'EmployeeTypes is already activated');
                        }
                    }
                }
            } else {
                throw new ErrorResponse(99998, "No Records Found");
            }
        } catch (error) {
            throw error;
        }
    }

    async getEmployeeTypesById(req: EmployeeTypesIdRequest): Promise<EmployeeTypesResponseModel> {
        try {
            const entity: EmployeeTypes = await this.repository.findOne({ where: { employeeTypesId: req.employeeTypesId } });
            if (entity) {
                const convertedEntity = this.adapter.convertEntityToDto(entity);
                const response: EmployeeTypesResponseModel = new EmployeeTypesResponseModel(true, 1, 'EmployeeTypes found and retrieved', convertedEntity);
                return response;
            } else {
                throw new ErrorResponse(99998, "No Data Found");
            }
        } catch (error) {
            throw error;
        }
    }
}
